import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef, Inject, LOCALE_ID } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormControl, FormArray } from '@angular/forms';
import { Task,TaskObject } from 'src/classes/task';
import { Delivery } from 'src/classes/delivery';
import { Contact } from 'src/classes/contact';
import { Schedule } from 'src/classes/schedule';
import { extractRuleFromScheduleData } from 'src/utils/extractRuleFromScheduleData';
import { PBIService } from 'src/services/pbi/pbi.service';
import { TaskService } from 'src/services/task/task.service';
import { Router } from '@angular/router';

interface DeliveryData {
  idDelivery: string | number;
  Subject: string;
  Message: string;
  Contacts: string | string[] | number[];
  Tags: string | string[];
  Method: string;
  Filters: string;
  Status: string;
}

interface ScheduleData {
  idSchedules: string | number;
  Type: 'H' | 'D' | 'W' | 'M';
  Rule: string;
  RuleString: string;
  Status: 'A' | 'D';
}

function deliveryDataToDelivery(data: DeliveryData): Delivery {
  if (typeof data.idDelivery === 'string') {
    data.idDelivery = parseInt(data.idDelivery, 10);
  }
  if (data.Contacts === undefined || data.Contacts === null) {
    data.Contacts = '';
  }
  if (typeof data.Contacts === 'string') {
    data.Contacts = data.Contacts.split(',').map(num => parseInt(num, 10));
  }
  if (data.Contacts instanceof Array && data.Contacts.length > 0 && typeof data.Contacts[0] !== 'number') {
    data.Contacts = (data.Contacts as string[]).map(contact => parseInt(contact.toString(), 10));
  }
  const d = new Delivery();
  d.idDelivery = Math.max(0, data.idDelivery); // negative values are indication of inexistant values
  delete d.idDelivery;
  d.contactId = data.Contacts as number[];
  d.type = data.Method;
  d.message = data.Message;
  d.tags = data.Tags instanceof Array ? data.Tags.join(" ") : data.Tags;
  d.subject = data.Subject;
  d.filters = data.Filters && data.Filters.length > 2 ? data.Filters : '[]';
  d.status = data.Status || 'A';
  return d;
}

function scheduleDataToSchedule(data: ScheduleData): Schedule {
  if (typeof data.idSchedules === 'string') {
    data.idSchedules = parseInt(data.idSchedules, 10);
  }
  const d = new Schedule();
  d.idSchedules = Math.max(0, data.idSchedules); // negative values are indication of inexistant values
  delete d.idSchedules; // not allowed ?
  d.rule = data.Rule;
  d.status = data.Status;
  d.type = data.Type;
  return d;
}

@Component({
  selector: 'app-task-form',
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.scss']
})
export class TaskFormComponent implements OnInit {
  @Input() stage: number;
  @Input() task: Task = new Task();
  @Input() contacts: Contact[];
  @Input() isLoadingContacts = false;
  @Input() showBackButtonFirstStage = false;
  @Input() availableTags: {tag: string, count: number}[] = [];

  @Output() changeStage = new EventEmitter<number>();
  @Output() addDelivery = new EventEmitter<Delivery>();
  @Output() updateDelivery = new EventEmitter<Delivery>();
  @Output() deleteDelivery = new EventEmitter<number>();
  @Output() addSchedule = new EventEmitter<Schedule>();
  @Output() updateSchedule = new EventEmitter<Schedule>();
  @Output() deleteSchedule = new EventEmitter<number>();
  @Output() addPage = new EventEmitter<TaskObject>();
  @Output() updatePage = new EventEmitter<TaskObject>();
  @Output() deletePage = new EventEmitter<TaskObject>();

  public taskForm: FormGroup;
  attemptedToSubmitInvalid = false;

  public workspaceList: {id: string; label: string, reports: {id: string; label: string}[]}[];
  public reportList: {id: string; label: string, pages: any[]}[];
  public pageList: {id: string; label: string}[];
  public pageLoadingSession: Promise<void> = undefined;
  public startedAtEnd = false;
  public stageError?: string | string[];
  public isSavingTask = false;
  public isLoadingWorkspace = false;
  private shouldResetCache = false;

  constructor(
    private formBuilder: FormBuilder,
    private pbiservice: PBIService,
    private taskService: TaskService,
    private router: Router,
    private ref: ChangeDetectorRef,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  ngOnInit() {
    if (typeof this.stage !== 'number') {
      this.stageError = 'Component Error: the \'stage\' property was not set';
    }
    this.taskForm = this.formBuilder.group({
      step0: this.formBuilder.group({
        TaskName: [this.task.name, Validators.required],
        PBIWorkspaceId: [this.task.PBIWorkspaceId, [Validators.required, Validators.minLength(2)]],
        PBIWorkspaceName: [this.getWorkspaceNameById(this.task.PBIWorkspaceId, ''), []],
        PBIReportId: [this.task.PBIReportId, [Validators.required, Validators.minLength(2)]],
        PBIReportName: [this.getReportNameById(this.task.PBIReportId, this.task.PBIReportName), []],
        PageList: this.formBuilder.array(
          this.task.objects.filter(i=>i.type=='I').map((obj: TaskObject) => this.formBuilder.group({
            name: [obj.name, Validators.required],
            type: [obj.type],
            page: [obj.page, Validators.required],
            objectId: [obj.objectId],
            reportOrder: [obj.reportOrder],
            pageView: [obj.pageView, Validators.required],
            width: [obj.width],
            height: [obj.height]
          }))
        ),
        TaskType: [this.task.type ? this.task.type : 'PNG', Validators.required],
        PdfPassword: [!!this.task.flagPdfPassword],
        ImageInBody: [!!this.task.flagImageInBody]
      }),
      step1: this.formBuilder.group({
        PageList: this.formBuilder.array(
          this.task.objects.filter(i=>i.type=='D').map((obj: TaskObject) => this.formBuilder.group({
            name: [obj.name, Validators.required],
            type: [obj.type],
            page: [obj.page, Validators.required],
            objectId: [obj.objectId, Validators.required],
            reportOrder: [obj.reportOrder],
            pageView: [obj.pageView],
            width: [obj.width],
            height: [obj.height]
          }))
        )
      }),
      step2: this.formBuilder.group({
        Deliveries: this.formBuilder.array(
          this.task.deliveryContacts.map((delivery: Delivery) => this.formBuilder.group({
            idDelivery: [delivery.idDelivery],
            Subject: [delivery.subject, Validators.required],
            Message: [delivery.message, Validators.required],
            Contacts: [delivery.contactId],
            Method: [delivery.type, Validators.required],
            Tags: [typeof delivery.tags === "string" ? delivery.tags.split(" ") : delivery.tags],
            Filters: [delivery.filters],
            Status: ['A']
          }))
        )
      }),
      step3: this.formBuilder.group({
        Schedules: this.formBuilder.array(
          this.task.schedules.map((schedule: Schedule) => this.formBuilder.group({
            idSchedules: [schedule.idSchedules],
            Type: [schedule.type, Validators.required],
            Rule: [schedule.rule, Validators.required],
            RuleString: [extractRuleFromScheduleData(schedule.type, schedule.rule)],
            Status: [schedule.status, Validators.required]
          }))
        )
      }),
    });
    this.isLoadingWorkspace = true;
    this.loadWorkspaceList().then(() => this.isLoadingWorkspace = false);
    if (this.stage === 4) {
      this.startedAtEnd = true;
      this.updateTaskObject();
    }
    /* DEBUG */ // this.stage = 2;
  }

  async onFormSubmit(values: any) {
    this.isSavingTask = true;
    // Populate this.task
    this.updateTaskObject();
    let id = this.task.id;
    delete this.task.id;
    let result;
    try {
      this.stageError = '';
      if (!this.task.status) {
        this.task.status = 'A';
      }
      if (id) {
        result = await this.taskService.editTask(this.task, id);
      } else {
        // Extra check to be extra sure we don't duplicate a task
        if (window.location.href.includes("/tasks/edit/")) {
          console.warn("Retrieved the ID of the task from the url");
          const parts = window.location.href.split("/").filter(a => a);
          if (isNaN(parseInt(parts[parts.length-1], 10))) {
            throw new Error("Could not retrieve the ID of the task from URL");
          }
          id = parseInt(parts[parts.length-1], 10);
        }
        result = await this.taskService.addTask(this.task);
      }
    } catch(err) {
      this.stageError = err && err.message ? err.message : 'Unknown error at form submit';
      this.isSavingTask = false;
      if (id) {
        this.task.id = id;
      }
      return;
    }
    if (id) {
      this.task.id = id;
    }
    this.isSavingTask = false;
    this.router.navigate(['/tasks']);
  }

  async onRefreshWorkspaceClick() {
    if (!this.isLoadingWorkspace) {
      this.workspaceList = undefined;
      this.reportList = undefined;
      this.pageList = undefined;
      this.isLoadingWorkspace = true;
      this.shouldResetCache = true;
      await this.loadWorkspaceList();
      this.isLoadingWorkspace = false;
    }
  }

  get isStageErrorList() {
    return (this.stageError instanceof Array && this.stageError.length > 0);
  }

  get isStageErrorString() {
    return (typeof this.stageError === 'string' && this.stageError.length > 0);
  }

  updateTaskObject() {
    this.task.name = this.step0Group.get('TaskName').value;
    this.task.type = this.step0Group.get('TaskType').value;
    this.task.flagPdfPassword = !!this.step0Group.get('PdfPassword').value;
    this.task.flagImageInBody = !!this.step0Group.get('ImageInBody').value;
    if (this.workspaceList) {
      this.task.PBIReportName = this.getReportNameById(this.step0Group.get('PBIReportId').value, '');
    }
    const pageListValue = this.step0Group.get('PageList').value;
    if (typeof pageListValue === 'string') {
      this.task.pages = pageListValue.split(",");
    } else if (pageListValue instanceof Array) {
      this.task.pages = pageListValue;
    } else {
      console.warn('Soft Error: Unhandled page list value:', pageListValue);
      this.task.pages = [];
    }
    this.task.PBIReportId = this.step0Group.get('PBIReportId').value;
    this.task.PBIWorkspaceId = this.step0Group.get('PBIWorkspaceId').value;
    this.task.deliveryContacts = this.step2Group.get('Deliveries').value.map(deliveryDataToDelivery);
    this.task.schedules = this.step3Group.get('Schedules').value.map(scheduleDataToSchedule);
  }

  async onWorkspaceListUpdate() {
    const workspaceId = this.step0Group.get('PBIWorkspaceId').value;
    const workspace = this.getWorkspaceById(workspaceId) || this.workspaceList[0];

    if (!workspace) {
      return;
    }

    this.step0Group.get('PBIWorkspaceId').setValue(workspace.id);
    await this.onWorkspaceChange(workspace.id);
  }

  async loadWorkspaceList() {
    this.workspaceList = undefined;
    try {
      const response = await this.pbiservice.getWorkspaceList(this.shouldResetCache);
      if (typeof response !== 'object' || !(response.list instanceof Array)) {
        throw new Error(`PBI Service's method to retrieve workspaces returned unhandled object`);
      }
      this.workspaceList = response.list.map(workspace => ({
        id: workspace.id,
        label: workspace.name,
        reports: undefined
      }));
      await this.onWorkspaceListUpdate();
    } catch(err) {
      if (typeof this.stageError !== 'string' || !this.stageError.startsWith('Error')) {
        this.stageError = 'Loading Workspace Error: ' + err.toString();
      }
      console.error(err);
      this.workspaceList = [];
      this.reportList = [];
      this.pageList = [];
    }
    this.ref.markForCheck();
  }

  addPageAbove(obj:TaskObject){
    this.stageError = '';
    this.addPage.emit(obj);
    const objects = this.step0Group.get('PageList') as FormArray;
    objects.push(this.formBuilder.group({
      name: [obj.name, Validators.required],
      type: ['I'],
      page: [obj.page, Validators.required],
      objectId: [''],
      reportOrder: [obj.reportOrder],
      pageView: [obj.pageView, Validators.required],
      width: [obj.width],
      height: [obj.height]
    }));
  }

  addObjectAbove(obj:TaskObject){
    this.stageError = '';
    this.addPage.emit(obj);
    const objects = this.step1Group.get('PageList') as FormArray;
    objects.push(this.formBuilder.group({
      name: [obj.name, Validators.required],
      type: ['D'],
      page: [obj.page, Validators.required],
      objectId: [obj.objectId],
      reportOrder: [obj.reportOrder],
      pageView: [obj.pageView, Validators.required],
      width: [obj.width],
      height: [obj.height]
    }));
  }

  updatePageAbove(obj: TaskObject) {
    this.updatePage.emit(obj);
    const objects = this.step0Group.get('PageList') as FormArray;
    for (const control of objects.controls) {
      if (!control.get('reportOrder')) {
        continue;
      }
      if (control.get('reportOrder').value !== obj.reportOrder) {
        continue;
      }
      control.get('name').setValue(obj.name);
      control.get('type').setValue(obj.type||'I');
      control.get('page').setValue(obj.page);
      control.get('pageView').setValue(obj.pageView);
      control.get('objectId').setValue(obj.objectId||'');
      control.get('width').setValue(obj.width);
      control.get('height').setValue(obj.height);
    }
  }

  updateObjectAbove(obj: TaskObject) {
    this.updatePage.emit(obj);
    const objects = this.step1Group.get('PageList') as FormArray;
    for (const control of objects.controls) {
      if (!control.get('objectId')) {
        continue;
      }
      if (control.get('reportOrder').value !== obj.reportOrder) {
        continue;
      }
      control.get('name').setValue(obj.name);
      control.get('type').setValue(obj.type||'D');
      control.get('page').setValue(obj.page);
      control.get('pageView').setValue(obj.pageView);
      control.get('objectId').setValue(obj.objectId);
      control.get('width').setValue(obj.width);
      control.get('height').setValue(obj.height);
    }
  }

  deletePageAbove(id: TaskObject) {
    this.deletePage.emit(id);
    const objects = this.step0Group.get('PageList') as FormArray;
    let index: number;
    objects.controls.some((control: FormControl, i: number) => {
      if (control.get('reportOrder').value === id.reportOrder) {
        index = i;
        return true;
      }
      return false;
    });
    if (index !== undefined && index !== null && index >= 0 && index < objects.controls.length) {
      objects.removeAt(index);
    }
  }


  deleteObjectAbove(id: TaskObject) {
    this.deletePage.emit(id);
    const objects = this.step1Group.get('PageList') as FormArray;
    let index: number;
    objects.controls.some((control: FormControl, i: number) => {
      if (control.get('reportOrder').value === id.reportOrder) {
        index = i;
        return true;
      }
      return false;
    });
    if (index !== undefined && index !== null && index >= 0 && index < objects.controls.length) {
      objects.removeAt(index);
    }
  }

  addDeliveryAbove(delivery: Delivery) {
    this.stageError = '';
    this.addDelivery.emit(delivery);
    const deliveries = this.step2Group.get('Deliveries') as FormArray;
    deliveries.push(this.formBuilder.group({
      idDelivery: [delivery.idDelivery],
      Subject: [delivery.subject, Validators.required],
      Message: [delivery.message, Validators.required],
      Contacts: [delivery.contactId],
      Method: [delivery.type, Validators.required],
      Tags: [typeof delivery.tags === "string" ? delivery.tags.split(" ") : delivery.tags],
      Filters: [delivery.filters],
      Status: ['A']
    }));
  }

  updateDeliveryAbove(delivery: Delivery) {
    this.updateDelivery.emit(delivery);
    const deliveries = this.step2Group.get('Deliveries') as FormArray;
    for (const control of deliveries.controls) {
      if (!control.get('idDelivery')) {
        continue;
      }
      if (control.get('idDelivery').value !== delivery.idDelivery) {
        continue;
      }
      control.get('idDelivery').setValue(delivery.idDelivery);
      control.get('Subject').setValue(delivery.subject);
      control.get('Message').setValue(delivery.message);
      control.get('Contacts').setValue(delivery.contactId);
      control.get('Method').setValue(delivery.type);
      control.get('Tags').setValue(delivery.tags);
      control.get('Filters').setValue(delivery.filters);
      control.get('Status').setValue(delivery.status || 'A');
    }
  }

  deleteDeliveryAbove(id: number) {
    this.deleteDelivery.emit(id);

    const deliveries = this.step2Group.get('Deliveries') as FormArray;
    let index: number;
    deliveries.controls.some((control: FormControl, i: number) => {
      if (control.get('idDelivery').value === id) {
        index = i;
        return true;
      }
      return false;
    });
    if (index !== undefined && index !== null && index >= 0 && index < deliveries.controls.length) {
      deliveries.removeAt(index);
    }
  }

  addScheduleAbove(schedule: Schedule) {
    console.log(schedule);
    this.stageError = '';
    this.addSchedule.emit(schedule);
    const schedules = this.step3Group.get('Schedules') as FormArray;
    schedules.push(this.formBuilder.group({
      idSchedules: [schedule.idSchedules],
      Type: [schedule.type, Validators.required],
      Rule: [schedule.rule, Validators.required],
      RuleString: [extractRuleFromScheduleData(schedule.type, schedule.rule)],
      Status: [schedule.status, Validators.required]
    }));
  }

  updateScheduleAbove(schedule: Schedule) {
    this.updateSchedule.emit(schedule);
    const schedules = this.step3Group.get('Schedules') as FormArray;
    for (const control of schedules.controls) {
      if (!control.get('idSchedules') || control.get('idSchedules').value !== schedule.idSchedules) {
        continue;
      }
      control.get('idSchedules').setValue(schedule.idSchedules);
      control.get('Type').setValue(schedule.type);
      control.get('Rule').setValue(schedule.rule);
      control.get('RuleString').setValue(extractRuleFromScheduleData(schedule.type, schedule.rule));
      control.get('Status').setValue(schedule.status);
    }
  }

  deleteScheduleAbove(id: number) {
    this.deleteSchedule.emit(id);

    const schedules = this.step3Group.get('Schedules') as FormArray;
    let index: number;
    schedules.controls.some((control: FormControl, i: number) => {
      if (control.get('idSchedules') && control.get('idSchedules').value === id) {
        index = i;
        return true;
      }
      return false;
    });
    if (index !== undefined && index !== null && index >= 0 && index < schedules.controls.length) {
      schedules.removeAt(index);
    }
  }

  get step0Group(): FormGroup { return this.taskForm.get('step0') as FormGroup; }
  get step1Group(): FormGroup { return this.taskForm.get('step1') as FormGroup; }
  get step2Group(): FormGroup { return this.taskForm.get('step2') as FormGroup; }
  get step3Group(): FormGroup { return this.taskForm.get('step3') as FormGroup; }

  onPreviousStageClick() {
    this.stageError = '';
    this.attemptedToSubmitInvalid = false;
    this.changeStage.emit(this.stage - 1);
  }

  onNextStageClick() {
    this.stageError = '';
    this.attemptedToSubmitInvalid = false;
    this.augmentErrorMessage();

    if (this.stage === 0 && this.step0Group.valid && !this.stageError) {
      this.task.PBIReportId = this.step0Group.get('PBIReportId').value;
      this.task.PBIWorkspaceId = this.step0Group.get('PBIWorkspaceId').value;
      this.changeStage.emit(this.stage + 1);
    }
    else if (this.stage === 1 && this.step1Group.valid && !this.stageError) {
      this.changeStage.emit(this.stage + 1);
    } else if (this.stage === 2 && this.step2Group.valid && !this.stageError) {
      this.changeStage.emit(this.stage + 1);
    } else if (this.stage === 3 && this.step3Group.valid && !this.stageError) {
      this.updateTaskObject();
      this.changeStage.emit(this.stage + 1);
    } else {
      this.attemptedToSubmitInvalid = true;
    }
  }

  isEnglishLocale() {
    return !(typeof this.locale === "string" && this.locale.toLowerCase().substring(0, 2) === "pt");
  }

  augmentErrorMessage() {
    if (this.stage === 0) {
      if (this.step0Group.get('TaskName').invalid) {
        this.step0Group.get('TaskName').markAsTouched();
        if (this.locale)

        this.stageError = this.isEnglishLocale() ? 'The task name is required' : 'O nome da tarefa é obrigatório';
      }
    }else if(this.stage === 1){
      const objs = this.step1Group.get('PageList') as FormArray;
      const objs0 = this.step0Group.get('PageList') as FormArray;
      if (objs.controls.length <= 0 && objs0.controls.length <= 0) {
        this.step1Group.get('PageList').markAsTouched();
        if (this.isEnglishLocale()) {
          this.stageError = (this.stageError ? this.stageError + ' and at ' : 'At ') + 'least a single report page or object data must be added';
        } else {
          this.stageError = (this.stageError ? this.stageError + ' e pelomenos ' : 'Pelomenos ') + ' uma pagina do relatório ou dados de objeto precisa ser adicionada';
        }
      }
    } else if (this.stage === 2) {
      const deliveries = this.step2Group.get('Deliveries') as FormArray;
      if (deliveries.controls.length <= 0) {
        this.stageError = this.isEnglishLocale() ? 'At least a single delivery must be created' : 'Pelomenos uma entrega precisa ser cadastrada';
      }
    } else if (this.stage === 3) {
      const schedules = this.step3Group.get('Schedules') as FormArray;
      if (schedules && schedules.controls.length <= 0) {
        this.stageError = this.isEnglishLocale() ? 'At least a single schedule must be created' : 'É necessário que exista pelomenos um cadastro na tarefa';
      }
    }
  }

  onFirstStageClick() {

    this.changeStage.emit(0);
  }

  getWorkspaceById(id: string, fallback?: any) {
    if (!this.workspaceList) {
      return fallback;
    }
    for (const workspace of this.workspaceList) {
      if (workspace.id !== id) {
        continue;
      }
      return workspace;
    }
    return fallback;
  }

  getWorkspaceNameById(id: string, fallback?: string) {
    const r = this.getWorkspaceById(id, undefined);
    return r ? r.label : fallback;
  }

  getReportById(id: string, fallback?: any) {
    if (!id || !this.workspaceList) {
      return fallback;
    }
    for (const workspace of this.workspaceList) {
      if (!workspace.reports) {
        continue;
      }
      for (const report of workspace.reports) {
        if (report.id !== id) {
          continue;
        }
        return report;
      }
    }
    return fallback;
  }

  getReportNameById(id: string, fallback?: string) {
    const r = this.getReportById(id, undefined);
    return r ? r.label : fallback;
  }

  getTypeLabel(typeCode: 'H' | 'D' | 'W' | 'M'): string {
    return {H: 'Hourly', D: 'Daily', W: 'Weekly', M: 'Monthly' }[typeCode] || 'Unknown';
  }

  async onWorkspaceChange(id: string) {
    const resetCache = this.shouldResetCache;
    try {
      if (!id) {
        this.reportList = [];
        this.pageList = [];
        return;
      }
      const workspace = this.getWorkspaceById(id);
      if (!workspace) {
        this.step0Group.get('PBIWorkspaceName').setValue("");
        this.reportList = [];
        this.pageList = [];
        return;
      }
      this.step0Group.get('PBIWorkspaceName').setValue(workspace.label);
      this.reportList = workspace.reports;

      // Load the report list if it is missing or empty
      if (resetCache || this.reportList === undefined || this.reportList.length === 0 || resetCache) {
        this.reportList = undefined;
        this.pageList = undefined;
        this.step0Group.get("PBIReportId").markAsUntouched();
        // Updates cache because we didn't know about this report list
        const { list } = await this.loadWorkspaceReportList(id, true);
        this.reportList = workspace.reports = list.map(reportEntry => ({
          id: reportEntry.id,
          label: reportEntry.name,
          pages: undefined
        }));
      }

      const reportId = this.step0Group.get('PBIReportId').value as string;
      if (this.reportList && this.reportList.some(report => report && report.id === reportId)) {
        // Select the value because it was found
        await this.onReportChange(reportId);
      } else {
        // Does not select anything because currently selected value was missing
        await this.onReportChange(null);
      }
    } catch(err) {
      err.message = "Workspace Change Failed: " + err.message;
      this.reportList = [];
      throw err;
    }
  }

  async loadWorkspaceReportList(workspaceId: string, resetCache: boolean) {
    return await this.pbiservice.getWorkspaceReportList(workspaceId, resetCache);
  }

  async onReportChange(reportId: string | null) {
    const resetCache = this.shouldResetCache;
    try {
      const report = this.getReportById(reportId);
      if (!report) {
        this.step0Group.get('PBIReportId').setValue('');
        this.step0Group.get('PBIReportName').setValue('');
        this.pageList = [];
        return;
      }

      this.step0Group.get('PBIReportName').setValue(report.label);

      this.pageList = report.pages;

      // Load the page list if it is missing or empty
      if (resetCache || this.pageList === undefined || this.pageList.length === 0 || resetCache) {
        report.pages = undefined;
        const workspaceId = this.step0Group.get('PBIWorkspaceId').value;
        // Updates cache because we didn't know about this page list
        const { list } = await this.loadWorkspaceReportPageList(workspaceId, reportId, true);
        this.pageList = report.pages = list.sort(
          (prev, next) => prev.order - next.order
        ).map(page => ({
            id: (page.Name || page.name) || page.id, label: page.displayName
        }));
      }
    } catch(err) {
      err.message = "Report Change Failed: " + err.message;
      this.pageList = [];
      throw err;
    }
  }

  async loadWorkspaceReportPageList(workspaceId: string, reportId: string, resetCache: boolean) {
    return await this.pbiservice.getReportPages(workspaceId, reportId, resetCache);
  }

  onBackClick(event: Event) {
    this.router.navigate(['/tasks']);
  }
}
